import React, { ReactNode, MouseEvent, KeyboardEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';

import { Button } from '../Button';

interface Props {
  children?: ReactNode;
  className?: string;
  closeWithOverlay?: boolean;
  isOpen: boolean;
  removePadding?: boolean;
  onClose?: () => void;
  // Pass `title` and/or `footer` to use the standard modal shell — close X at
  // top-right, h3 title, body, and right-aligned footer (matches the
  // "cancel your membership?" modal used as the migration baseline).
  // Callers that need a custom header bar can omit `title` and pass
  // `showCloseButton={false}` to suppress the default X.
  title?: ReactNode;
  footer?: ReactNode;
  showCloseButton?: boolean;
}

export const Modal = ({
  children,
  className,
  closeWithOverlay = true,
  isOpen,
  onClose,
  removePadding,
  title,
  footer,
  showCloseButton,
}: Props): JSX.Element => {
  const handleOverlayClick = (e: MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget && onClose && closeWithOverlay) {
      onClose();
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Escape' && onClose) {
      onClose();
    }
  };

  // Render the standard close X when title/footer is used (the "shell"
  // pattern) or when explicitly requested. Older callers that build their
  // own header keep their existing layout untouched.
  const renderCloseButton =
    showCloseButton ?? (onClose != null && (title != null || footer != null));

  return (
    <div className={`${isOpen ? 'block' : 'hidden'} flex fixed top-0 bottom-0 left-0 right-0 z-[1003] bg-neutral-900/50`}
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
      role='presentation'
      tabIndex={-1}
    >
      <div
        role='dialog'
        aria-modal='true'
        aria-label='Modal'
        className={`${className ?? ''} relative max-h-screen shadow sm:rounded-lg ${!removePadding ? 'p-4 sm:p-10': ''} bg-white overflow-auto m-auto`}
      >
        {renderCloseButton && onClose && (
          <Button
            aria-label='Close'
            onClick={onClose}
            className='absolute right-2 top-2 text-gray-400 hover:text-gray-500 transition-colors bg-transparent border-0 p-1 leading-none'
          >
            <FontAwesomeIcon icon={faXmark} className='w-5 h-5' />
          </Button>
        )}
        {title && <h3 className='mb-2 font-semibold pr-8'>{title}</h3>}
        {footer ? (
          <>
            <div className='mb-4'>{children}</div>
            <div className='flex justify-end gap-6'>{footer}</div>
          </>
        ) : (
          children
        )}
      </div>
    </div>
  );
};
